<template>
  <div class="stock-checker__content">
    <div class="stock-checker__header">
      <span
        class="text-body-sbold"
        v-text="$string('product.stock_checker.title')"
      />
      <button
        :aria-label="$string('accessibility.close')"
        class="stock-checker__close-button"
        @click="$emit('close')"
      >
        <icon-close />
      </button>
    </div>

    <div
      v-if="state !== 'missing variant'"
      class="stock-checker__main"
      js-overlay="scrollElement"
    >
      <div class="stock-checker__product-details">
        <responsive-image
          :class="$blockClass(blockClass, 'stock-checker-image')"
          block-class="stock-checker"
          :image="productDetailsImage"
          min-max="64-80"
          ratio="1:1"
        />

        <p
          class="
            stock-checker__product-title
            text-body-xs
            text-body-s-desktop
          "
          v-text="product.title"
        />
      </div>

      <div
        v-for="(store, index) in getCurrentVariantStoreAvailability"
        :key="`store-details-${index}`"
        class="stock-checker__store-details"
        :class="[`stock-checker__store-details--${index}`]"
      >
        <span
          v-if="store.location.name"
          class="
            stock-checker__store-name
            text-body-sbold
          "
          v-text="store.location.name"
        />

        <span
          class="
            stock-checker__availability
            text-body-xsbold
          "
          :class="[store.available ? 'in-stock' : 'sold-out']"
          v-text="store.available
            ? $string('product.stock_checker.in_stock')
            : $string('product.stock_checker.sold_out')
          "
        />
      </div>
    </div>

    <div
      v-else
      class="
        stock-checker__main
        stock-checker__error
      "
    >
      <span
        class="stock-checker__error-message text-body-sbold"
        v-text="$string('product.stock_checker.missing_variant')"
      />
    </div>
  </div>
</template>

<script>

/**
 * Vue: Stock checker (stock-checker)
 * -----------------------------------------------------------------------------
 * Overlay modal used for checking a products stock within all the store locations.
 *
 * @param {Object|Boolean} product - Canvas formatted product object.
 * @param {Object|Boolean} variant - Canvas formatted variant object.
 *
 */
import IconClose from '~icons/misc/close.svg'

import ResponsiveImage from '~global/images/responsive-image'

import storeAvailability from '~/graphql/queries/storeAvailability.gql'
import { transformEdges } from '~/helpers/graphql'

export default {
  name: 'StockChecker',

  components: {
    IconClose,
    ResponsiveImage,
  },

  props: {
    product: {
      type: [Object, Boolean],
      default: false,
    },
    variant: {
      type: [Object, Boolean],
      default: false,
    },
  },

  emits: ['close'],

  data() {
    return {
      stores: [],
      state: 'loading',
    }
  },

  computed: {

    /**
     * Compute product details image.
     * @returns {Object}
     */
    productDetailsImage() {
      return this.variant?.image || this.product.featuredImage
    },

    /**
     * Compute current variant's store availability.
     * @returns {Object}
     */
    getCurrentVariantStoreAvailability() {
      if (!this.stores.length || !this.variant) {
        return
      }

      const currentVariantStoreData = this.stores.find((store) => store.variantId.includes(this.variant.id))

      if (!currentVariantStoreData) {
        return
      }

      return currentVariantStoreData.storeAvailability
    },
  },

  mounted() {
    if (!this.variant) {
      this.state = 'missing variant'
      return
    }

    if (!this.product) {
      return
    }

    this.queryStoreAvailability()
  },

  methods: {

    /**
     * Query variant store availability from GraphQL API.
     */
    async queryStoreAvailability() {
      if (this.stores.length && this.stores[0].productId === this.product.id) {
        return
      }

      try {
        const response = await cnvs.Query({
          locale: cnvs.store.language,
          query: storeAvailability,
          variables: {
            country: cnvs.store.country,
            handle: this.product.handle,
          },
        })

        const formattedVariants = transformEdges(response.product.variants)

        const formattedStoreData = []

        formattedVariants.forEach((data) => {
          const storeData = {
            variantId: data.id,
            productId: this.product.id,
            storeAvailability: transformEdges(data.storeAvailability),
          }

          formattedStoreData.push(storeData)
        })

        /**
         * If no products returned then error.
         */
        if (!formattedStoreData.length) {
          this.state = 'error'
          return
        }

        this.stores = formattedStoreData
        this.state = 'ready'

      } catch(error) {
        this.state = 'error'
      }
    },
  },
}
</script>

<style lang="scss">
@import './stock-checker';
</style>
